import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useCitasEnVivo } from './useCitasEnVivo'
import { idDelDispositivo } from '../utils/dispositivo'
// t478 · varias cuentas en el mismo navegador: el aviso de OTRA cuenta lo abre components/CuentasGlobales (pasa a ella).
import { rutaParaAbrirAviso, idsDeLasDormidas, apuntarLasDormidas } from '../utils/cuentasDelDispositivo'

// LAS CLAVES VAN POR USUARIO. En recepcion el mismo ordenador lo usan varias personas: con una clave
// unica, la marca de "hasta aqui ya avise" de una se la comia la siguiente, y los mensajes que le
// habian sonado a una le volvian a sonar a la otra (o peor: no le sonaban).
const quienSoy = () => {
  try { return String(JSON.parse(localStorage.getItem('user') || '{}')?.id || 'anon') } catch { return 'anon' }
}
const KEY_LAST_CHECK = () => 'notif_last_check:' + quienSoy()
// EN localStorage, NO EN sessionStorage. sessionStorage es de CADA PESTAÑA: abrir una segunda
// pestaña, o recargar en algunos navegadores, empezaba de cero y volvia a anunciar —con sonido— los
// mismos mensajes que ya se habian visto. Esa era la mitad de los avisos duplicados.
const KEY_NOTIFIED = () => 'notif_ids_notificados:' + quienSoy()
const POLL_INTERVAL_MS = 8000
// UN AVISO VIEJO NO ES UN AVISO. Si la app ha estado cerrada un rato (una noche, un fin de semana),
// `desde` seguia apuntando a la ultima vez que se miro y al abrir caian de golpe hasta veinte
// notificaciones de mensajes de hace dias, todas sonando. Lo que ha pasado mientras no se miraba se
// ve en la lista de conversaciones, que para eso lleva el contador de no leidos; una notificacion es
// para lo que esta pasando AHORA. Pasado este margen se empieza de cero.
const MAX_DESFASE_MS = 10 * 60 * 1000

export function useNotifications() {
  // B-048 · las citas en vivo: el aviso del servidor cuando cambian las citas de esta clínica (hooks/useCitasEnVivo).
  useCitasEnVivo()

  const navigate = useNavigate()
  const intervalRef = useRef(null)
  const channelRef = useRef(null)
  const audioRef = useRef(null)

  useEffect(() => {
    // En la app nativa (Capacitor) el push lo gestiona FCM: no registramos Service Worker / Web Push
    // ni el sondeo local con Notification API, para no duplicar el aviso en el móvil (FCM + web).
    if (window.Capacitor?.isNativePlatform?.()) return
    // 1) Registrar Service Worker
    if (!('serviceWorker' in navigator)) {
      console.warn('[Notif] Navegador sin soporte Service Worker')
      return
    }
    
    navigator.serviceWorker.register('/sw.js').then(reg => {
      console.log('[Notif] SW registrado:', reg.scope)
      // 2) Pedir permiso y suscribir a Web Push (notificaciones con la app CERRADA).
      const trasPermiso = (perm) => { if (perm === 'granted') suscribirWebPush(reg) }
      if ('Notification' in window) {
        if (Notification.permission === 'granted') suscribirWebPush(reg)
        else if (Notification.permission === 'default') Notification.requestPermission().then(trasPermiso)
      }
    }).catch(e => console.error('[Notif] Error registrando SW:', e))

    // 3) BroadcastChannel para coordinar entre pestañas (evita duplicados)
    if ('BroadcastChannel' in window) {
      channelRef.current = new BroadcastChannel('notif-clinica')
      channelRef.current.onmessage = (e) => {
        if (e.data?.type === 'notified') {
          marcarNotificado(e.data.msgId)
        }
      }
    }

    // 4) Audio precargado
    audioRef.current = new Audio('/notif.mp3')
    audioRef.current.volume = 0.4  // suave

    // 5) Escuchar mensajes del SW (cuando el usuario clica una notif)
    const swMessageHandler = (event) => {
      if (event.data?.type === 'open-conversation') {
        navigate('/comunicacion?conv=' + event.data.conversacionId)
      } else if (event.data?.type === 'navigate' && event.data.ruta && !rutaParaAbrirAviso(event.data.cuenta, event.data.ruta)) {
        navigate(event.data.ruta)
      }
    }
    navigator.serviceWorker.addEventListener('message', swMessageHandler)

    // 6) Polling
    const fetchPendientes = async () => {
      try {
        // Sin fallback en JS (que sería UTC): en el primer sondeo NO mandamos `desde` y el servidor
        // usa su propio reloj (LOCALTIMESTAMP-60s), evitando el desfase UTC↔Berlin que colaba avisos
        // viejos (~2h) al arrancar una sesión nueva. Después reenviamos el server_time que él devuelve.
        // Si la marca es de hace mas de MAX_DESFASE_MS no se manda: el servidor cae a su propia
        // ventana corta y no llega la avalancha de lo que paso mientras la app estaba cerrada.
        let desde = localStorage.getItem(KEY_LAST_CHECK())
        if (desde) {
          const t = Date.parse(desde)
          if (!Number.isFinite(t) || (Date.now() - t) > MAX_DESFASE_MS) desde = null
        }
        const r = await api.get('/conversaciones/pendientes-notificar' + (desde ? '?desde=' + encodeURIComponent(desde) : ''))
        const { mensajes = [], server_time } = r.data || {}
        // t515 · lo que la clínica ha elegido para esta persona (Notificaciones → Configurar): aviso del navegador y sonido.
        // Sin `aviso` (un servidor de antes), como siempre: los dos.
        const conAviso = r.data?.aviso?.push !== false
        const conSonido = r.data?.aviso?.sonido !== false
        if (server_time) localStorage.setItem(KEY_LAST_CHECK(), server_time)
        
        for (const m of mensajes) {
          if (yaNotificado(m.msg_id)) continue
          // Evitar notificar si la pestaña está mirando esa conv
          const url = new URL(window.location.href)
          const convActiva = url.searchParams.get('conv')
          if (document.visibilityState === 'visible' && convActiva === String(m.conversacion_id)) {
            marcarNotificado(m.msg_id)
            continue
          }
          // Mostrar notificación via SW
          mostrarNotificacion(m, { conAviso, conSonido })
          marcarNotificado(m.msg_id)
          // Avisar a otras pestañas para que no notifiquen de nuevo
          if (channelRef.current) channelRef.current.postMessage({ type: 'notified', msgId: m.msg_id })
        }
      } catch(e) { 
        // t85 (4-oct-2026) · 403 = a este rol no le tocan los mensajes de WhatsApp (un doctor, por ejemplo): se deja de
        // preguntar. Antes se repetía cada 8 segundos toda la sesión, 450 peticiones fallidas por hora y persona.
        if (e.response?.status === 403) { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null } return }
        // Silenciar errores de red (suelen ser de auth caducado o offline)
        if (e.response?.status !== 401) console.error('[Notif] Error fetch:', e.message)
      }
    }

    fetchPendientes()  // primera vez al cargar
    intervalRef.current = setInterval(fetchPendientes, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (channelRef.current) channelRef.current.close()
      navigator.serviceWorker.removeEventListener('message', swMessageHandler)
    }
  }, [navigate])
}

// t478 (26-oct-2026) · EL PANEL DE PLATAFORMA TAMBIÉN APUNTA ESTE NAVEGADOR A LOS AVISOS. Solo lo hacían los dos armazones
// de la clínica (Layout y AdminLayout, con el hook de arriba), así que una cuenta SOLO de plataforma, que trabaja en
// Superadmin, no recibía nunca el aviso de soporte en el ordenador con el panel cerrado. Esto hace la parte del aviso con la
// app cerrada (el service worker, el permiso y la suscripción) y nada más: ni el sondeo de WhatsApp ni las citas en vivo,
// que son de una clínica. Lo carga components/PlataformaLayout aparte (import dinámico). En la app nativa no hace nada (allí
// el aviso va por Firebase o Apple: push/registrarPush).
export async function apuntarEsteNavegadorALosAvisos() {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return false
    if (!('serviceWorker' in navigator)) return false
    const reg = await navigator.serviceWorker.register('/sw.js')
    if (!('Notification' in window)) return false
    let perm = Notification.permission
    if (perm === 'default') perm = await Notification.requestPermission()
    if (perm !== 'granted') return false
    await suscribirWebPush(reg)
    return true
  } catch (e) {
    console.warn('[Notif] No se ha podido apuntar este navegador a los avisos:', e && e.message)
    return false
  }
}

// Suscribe el navegador a Web Push (VAPID) y manda la suscripción al backend. Idempotente: si ya está
// suscrito, reenvía la suscripción (por si el token cambió o falta en el servidor). No-op si el
// navegador no soporta PushManager o no hay clave VAPID configurada en el backend.
async function suscribirWebPush(reg) {
  try {
    // En la app nativa (Capacitor) el push llega por FCM: NO suscribir también Web Push
    // en el webview, o el móvil recibiría el aviso DOS veces (FCM + web).
    if (window.Capacitor?.isNativePlatform?.()) return
    if (!('PushManager' in window) || !reg?.pushManager) return
    const { data } = await api.get('/push/vapid-public')
    const key = data?.key
    if (!key) return   // Web Push no configurado en el servidor todavía
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(key) })
    }
    // 'web-movil' si es el navegador de un MÓVIL: el backend NO le manda Web Push si ese mismo agente
    // ya tiene la app NATIVA (evita el aviso duplicado FCM + Web Push en el mismo teléfono). El
    // navegador de ESCRITORIO va como 'web' normal y siempre recibe.
    const esMovil = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
    // t398 · con el identificador de este navegador (utils/dispositivo): el Busca no cuenta dos veces su aviso y su pestaña.
    // t478 · con las demás cuentas abiertas en este navegador (`cuentas`), para que el servidor no les quite el aviso; y
    // después, el mismo navegador apuntado con la sesión de cada una de ellas (utils/cuentasDelDispositivo).
    const plat = esMovil ? 'web-movil' : 'web'
    let cuentas = []
    try { cuentas = idsDeLasDormidas() } catch (_) { cuentas = [] }
    await api.post('/push/registrar', { token: JSON.stringify(sub), plataforma: plat, dispositivo: idDelDispositivo(), ...(cuentas.length ? { cuentas } : {}) })
    if (cuentas.length) apuntarLasDormidas({ token: JSON.stringify(sub), plataforma: plat, dispositivo: idDelDispositivo() }).catch(() => {})
  } catch (e) {
    console.warn('[Notif] No se pudo suscribir a Web Push:', e.message)
  }
}

function urlB64ToUint8(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

function yaNotificado(msgId) {
  try {
    const set = JSON.parse(localStorage.getItem(KEY_NOTIFIED()) || '[]')
    return set.includes(msgId)
  } catch { return false }
}

function marcarNotificado(msgId) {
  try {
    const set = JSON.parse(localStorage.getItem(KEY_NOTIFIED()) || '[]')
    if (set.includes(msgId)) return
    set.push(msgId)
    // Mantener solo los últimos 200
    localStorage.setItem(KEY_NOTIFIED(), JSON.stringify(set.slice(-200)))
  } catch {}
}

function mostrarNotificacion(m, { conAviso = true, conSonido = true } = {}) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  // Reproducir sonido
  if (conSonido) {
    try {
      const a = new Audio('/notif.mp3')
      a.volume = m.categoria === 'urgentes' ? 0.7 : 0.4
      a.play().catch(()=>{})
    } catch {}
  }
  if (!conAviso) return
  
  // Construir título según categoría
  const ICON = {
    urgentes: 'URGENTE',
    para_luz: '1ª visita',   // #10 · rol, no un nombre de persona (multi-clínica)
    no_asignadas: 'Conversación nueva',
    sin_leer: 'Mensaje nuevo'
  }
  const titulo = ICON[m.categoria] || 'Nuevo mensaje de un paciente'
  // 6-oct-2026 · CONTENIDO MÍNIMO, como los avisos que manda el servidor (backend/services/avisoMovil): esta notificación
  // del sistema se queda en el centro de avisos del ordenador y se ve sin entrar en Biango. Ni el nombre, ni el teléfono,
  // ni lo que ha escrito el paciente: eso se lee al abrir la conversación (adonde lleva al tocarla).
  const body = 'Un paciente ha escrito. Ábrelo en Biango para leerlo.'
  
  // Enviar al SW para mostrar
  navigator.serviceWorker.ready.then(reg => {
    reg.active?.postMessage({
      type: 'show-notification',
      titulo,
      body,
      conversacionId: m.conversacion_id,
      categoria: m.categoria,
      msgId: m.msg_id
    })
  })
}
